(() => {
  const storageKey = 'rr_recently_viewed_v1';
  const expiry = 30 * 24 * 60 * 60 * 1000;
  const cache = new Map();
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(value);
  let memory = [];

  function readHistory() {
    try {
      const raw = localStorage.getItem(storageKey);
      memory = raw === null ? [] : JSON.parse(raw);
    } catch {}
    if (!Array.isArray(memory)) memory = [];
    const seen = new Set();
    memory = memory.filter(entry => {
      if (!entry || !validHandle(entry.handle) || !Number.isFinite(entry.viewedAt)) return false;
      if (entry.viewedAt > Date.now() || Date.now() - entry.viewedAt > expiry || seen.has(entry.handle)) return false;
      seen.add(entry.handle);
      return true;
    }).slice(0, 16);
    return memory;
  }

  function recordProduct() {
    const source = document.getElementById('rr-viewed-product');
    if (!source) return;
    try {
      const current = JSON.parse(source.textContent);
      if (current.excluded || !validHandle(current.handle)) return;
      memory = [{ handle: current.handle, viewedAt: Date.now() }, ...readHistory().filter(entry => entry.handle !== current.handle)].slice(0, 16);
      try { localStorage.setItem(storageKey, JSON.stringify(memory)); } catch {}
    } catch {}
  }

  async function getProduct(root, handle) {
    const url = new URL(root + 'products/' + encodeURIComponent(handle) + '.js', location.origin);
    if (cache.has(url.href)) return cache.get(url.href);
    const promise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 6000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) return null;
        const product = await response.json();
        const tags = Array.isArray(product.tags) ? product.tags : [];
        if (product.handle !== handle || !product.featured_image || tags.includes('secret')) return null;
        return product;
      } catch {
        cache.delete(url.href);
        return null;
      } finally {
        clearTimeout(timeout);
      }
    })();
    cache.set(url.href, promise);
    return promise;
  }

  recordProduct();

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      this.list = this.querySelector('.rr-recently-viewed__list');
      this.controls = this.querySelector('.rr-recently-viewed__controls');
      this.previous = this.querySelector('[data-direction="-1"]');
      this.next = this.querySelector('[data-direction="1"]');
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      const signal = this.events.signal;
      this.querySelectorAll('[data-direction]').forEach(button => button.addEventListener('click', () => {
        const direction = getComputedStyle(this.list).direction === 'rtl' ? -1 : 1;
        this.list.scrollBy({ left: this.list.clientWidth * 0.85 * Number(button.dataset.direction) * direction, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }, { signal }));
      this.list.addEventListener('scroll', () => this.updateControls(), { passive: true, signal });
      this.list.addEventListener('focusin', event => {
        if (event.target.closest('a')) event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }, { signal });
      window.addEventListener('storage', event => {
        if (event.key === storageKey || event.key === null) this.load();
      }, { signal });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.list);
      }
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.intersectionObserver.disconnect();
            this.load();
          }
        }, { rootMargin: '300px 0px' });
        this.intersectionObserver.observe(this.parentElement);
      } else this.load();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.generation = (this.generation || 0) + 1;
    }

    async load() {
      const generation = this.generation = (this.generation || 0) + 1;
      const limit = Math.max(4, Math.min(12, Number(this.dataset.limit) || 8));
      const handles = readHistory().filter(entry => entry.handle !== this.dataset.current).slice(0, limit);
      const products = await Promise.all(handles.map(entry => getProduct(this.root, entry.handle)));
      if (!this.isConnected || this.generation !== generation) return;
      const fragment = document.createDocumentFragment();
      products.filter(Boolean).forEach(product => {
        const item = document.createElement('li');
        item.className = 'rr-recently-viewed__item';
        const link = document.createElement('a');
        link.className = 'rr-recently-viewed__link';
        link.href = this.root + 'products/' + encodeURIComponent(product.handle);
        link.title = product.title;
        link.setAttribute('aria-label', product.title);
        const image = document.createElement('img');
        const imageUrl = new URL(product.featured_image, location.origin);
        if (imageUrl.protocol !== 'https:' && imageUrl.protocol !== 'http:') return;
        imageUrl.searchParams.set('width', '320');
        image.src = imageUrl.href;
        image.alt = product.title;
        image.width = 160;
        image.height = 160;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.className = 'rr-recently-viewed__image';
        link.append(image);
        item.append(link);
        fragment.append(item);
      });
      this.list.replaceChildren(fragment);
      this.hidden = this.list.children.length === 0;
      this.list.scrollLeft = 0;
      this.updateControls();
    }

    updateControls() {
      const maximum = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
      const position = Math.abs(this.list.scrollLeft);
      this.controls.hidden = maximum <= 2;
      this.previous.disabled = position <= 2;
      this.next.disabled = position >= maximum - 2;
    }
  }

  if (!customElements.get('rr-recently-viewed')) customElements.define('rr-recently-viewed', RecentlyViewed);
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    recordProduct();
    document.querySelectorAll('rr-recently-viewed').forEach(element => element.load());
  });
})();